import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Heart, Users, Shield, User, Zap } from 'lucide-react';
import OnboardingModal from '@/components/OnboardingModal';

const HomePage = () => {
  const navigate = useNavigate();

  const themes = [
    {
      id: 'healing',
      title: 'Healing',
      description: 'Journey to self-love and recovery',
      icon: Heart,
      color: '#D4A5A0',
      gradient: 'from-[#D4A5A0] to-[#E7C6C2]',
      textColor: 'text-white',
      quoteCount: 150
    },
    {
      id: 'strength',
      title: 'Strength',
      description: 'Celebrate your resilience',
      icon: Zap,
      color: '#6C4CA2',
      gradient: 'from-[#6C4CA2] to-[#8B6BB8]',
      textColor: 'text-white',
      quoteCount: 150
    },
    {
      id: 'sisterhood',
      title: 'Sisterhood',
      description: 'We rise together',
      icon: Users,
      color: '#8A9B7A',
      gradient: 'from-[#8A9B7A] to-[#AAB49F]',
      textColor: 'text-white',
      quoteCount: 150
    },
    {
      id: 'empowerment',
      title: 'Empowerment',
      description: 'Reclaim your power',
      icon: Shield,
      color: '#7C9069',
      gradient: 'from-[#7C9069] to-[#AAB49F]',
      textColor: 'text-white',
      quoteCount: 150
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7F5F8] via-[#ECE8EF] to-[#E7C6C2]">
      <OnboardingModal />
      
      {/* Top Right Buttons */}
      <div className="fixed top-6 right-6 z-50 flex gap-3">
        <Button
          onClick={() => navigate('/profile')}
          className="bg-white hover:bg-gray-50 text-[#3E324D] px-5 py-3 rounded-full shadow-lg hover:shadow-xl flex items-center gap-2 border-2 border-[#E7C6C2]"
        >
          <User className="w-5 h-5" />
          <span className="hidden sm:inline font-medium">My Haven</span>
        </Button>
        <Button
          onClick={() => navigate('/favorites')}
          className="bg-[#E7C6C2] hover:bg-[#F7E5E5] text-[#3E324D] px-5 py-3 rounded-full shadow-lg hover:shadow-xl flex items-center gap-2"
        >
          <Heart className="w-5 h-5" fill="#3E324D" />
          <span className="hidden sm:inline font-medium">Close to My Heart</span>
        </Button>
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-6 py-12">
        <div className="text-center mb-16 animate-fade-in">
          <div className="inline-flex items-center gap-2 mb-6 cursor-pointer relative group" data-emergency-trigger>
            <Heart className="w-8 h-8 text-[#6C4CA2]" fill="#6C4CA2" />
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#3E324D]">
              SheSafe Haven
            </h1>
            {/* Subtle emergency exit hint */}
            <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="bg-[#3E324D] text-[#EDE8F3] text-xs px-3 py-1 rounded-full whitespace-nowrap">
                Double-tap for emergency exit
              </div>
            </div>
          </div>
          
          {/* Personalized greeting */}
          {(() => {
            const profile = localStorage.getItem('user_profile');
            if (profile) {
              const { name, avatar_emoji } = JSON.parse(profile);
              const displayName = name || 'love';
              return (
                <p className="text-xl text-[#6C4CA2] mb-4">
                  {avatar_emoji && <span className="mr-2">{avatar_emoji}</span>}
                  Welcome back, {displayName}
                </p>
              );
            }
            return null;
          })()}
          <p className="text-base sm:text-lg text-[#6E6275] max-w-3xl mx-auto mb-4 leading-relaxed">
            Hey love, I'm so glad you're here.
          </p>
          <p className="text-base sm:text-lg text-[#6E6275] max-w-3xl mx-auto mb-4 leading-relaxed">
            You're stepping into a chapter where your power, your voice, and your peace finally matter.
          </p>
          <p className="text-base sm:text-lg text-[#6E6275] max-w-3xl mx-auto mb-8 leading-relaxed">
            Whether you're walking away from a narcissistic partner, healing after a divorce, navigating single motherhood, 
            or rediscovering yourself after a painful breakup, this is your space. A space where you're not judged, not rushed, 
            and never alone. A space where your strength has room to breathe again.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Button
              onClick={() => navigate('/daily-quote')}
              className="bg-[#E7C6C2] hover:bg-[#F7E5E5] text-[#3E324D] px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl font-semibold"
            >
              <span className="text-xl mr-2">✨</span> Today's Whisper
            </Button>
            <Button
              data-testid="explore-all-btn"
              onClick={() => navigate('/reels')}
              className="bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl"
            >
              Begin Your Path
            </Button>
          </div>
          
          {/* Quick Access - Redesigned for Better Layout */}
          <div className="max-w-5xl mx-auto mt-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <button
                onClick={() => navigate('/inner-voice')}
                className="group relative overflow-hidden bg-gradient-to-br from-white to-[#F7F5F8] hover:from-[#E7C6C2]/30 hover:to-[#F7E5E5] border-3 border-[#E7C6C2]/40 hover:border-[#E7C6C2] px-4 py-5 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 transform"
              >
                <div className="flex flex-col items-center gap-2">
                  <div className="bg-[#E7C6C2]/20 group-hover:bg-[#E7C6C2]/40 p-3 rounded-full transition-all">
                    <span className="text-3xl group-hover:scale-110 transition-transform block">📔</span>
                  </div>
                  <span className="text-[#3E324D] font-bold text-sm text-center">Inner Voice</span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              </button>
              
              <button
                onClick={() => navigate('/journey')}
                className="group relative overflow-hidden bg-gradient-to-br from-white to-[#F7F5F8] hover:from-[#E7C6C2]/30 hover:to-[#F7E5E5] border-3 border-[#E7C6C2]/40 hover:border-[#E7C6C2] px-4 py-5 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 transform"
              >
                <div className="flex flex-col items-center gap-2">
                  <div className="bg-[#E7C6C2]/20 group-hover:bg-[#E7C6C2]/40 p-3 rounded-full transition-all">
                    <span className="text-3xl group-hover:scale-110 transition-transform block">📖</span>
                  </div>
                  <span className="text-[#3E324D] font-bold text-sm text-center">Your Journey</span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              </button>
              
              <button
                onClick={() => navigate('/milestones')}
                className="group relative overflow-hidden bg-gradient-to-br from-white to-[#F7F5F8] hover:from-[#E7C6C2]/30 hover:to-[#F7E5E5] border-3 border-[#E7C6C2]/40 hover:border-[#E7C6C2] px-4 py-5 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 transform"
              >
                <div className="flex flex-col items-center gap-2">
                  <div className="bg-[#E7C6C2]/20 group-hover:bg-[#E7C6C2]/40 p-3 rounded-full transition-all">
                    <span className="text-3xl group-hover:scale-110 transition-transform block">🏆</span>
                  </div>
                  <span className="text-[#3E324D] font-bold text-sm text-center">Milestones</span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              </button>
              
              <button
                onClick={() => navigate('/audio')}
                className="group relative overflow-hidden bg-gradient-to-br from-white to-[#F7F5F8] hover:from-[#E7C6C2]/30 hover:to-[#F7E5E5] border-3 border-[#E7C6C2]/40 hover:border-[#E7C6C2] px-4 py-5 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 transform"
              >
                <div className="flex flex-col items-center gap-2">
                  <div className="bg-[#E7C6C2]/20 group-hover:bg-[#E7C6C2]/40 p-3 rounded-full transition-all">
                    <span className="text-3xl group-hover:scale-110 transition-transform block">🌙</span>
                  </div>
                  <span className="text-[#3E324D] font-bold text-sm text-center leading-tight">Moments of<br/>Stillness</span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              </button>
              
              <button
                onClick={() => navigate('/whisper-back')}
                className="group relative overflow-hidden bg-gradient-to-br from-white to-[#F7F5F8] hover:from-[#E7C6C2]/30 hover:to-[#F7E5E5] border-3 border-[#E7C6C2]/40 hover:border-[#E7C6C2] px-4 py-5 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 transform col-span-2 sm:col-span-1"
              >
                <div className="flex flex-col items-center gap-2">
                  <div className="bg-[#E7C6C2]/20 group-hover:bg-[#E7C6C2]/40 p-3 rounded-full transition-all">
                    <span className="text-3xl group-hover:scale-110 transition-transform block">💬</span>
                  </div>
                  <span className="text-[#3E324D] font-bold text-sm text-center">Whisper Back</span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              </button>
            </div>
          </div>
        </div>

        {/* Theme Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {themes.map((theme, index) => {
            const IconComponent = theme.icon;
            return (
              <button
                key={theme.id}
                data-testid={`theme-card-${theme.id}`}
                className="group relative overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl cursor-pointer border-4 border-transparent hover:border-white/30 transform hover:scale-105 transition-all duration-300 w-full text-left"
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
                onClick={() => navigate(`/reels/${theme.id}`)}
              >
                {/* Background Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient} opacity-95 group-hover:opacity-100 transition-opacity duration-300`}></div>
                
                {/* Content */}
                <div className="relative p-8 h-56 flex flex-col justify-between">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className={`text-4xl font-bold ${theme.textColor} mb-3 drop-shadow-lg`}>
                        {theme.title}
                      </h3>
                      <p className={`${theme.textColor} text-lg font-medium drop-shadow-md`}>
                        {theme.description}
                      </p>
                    </div>
                    <div className="bg-white/30 backdrop-blur-sm p-4 rounded-2xl group-hover:bg-white/40 transition-all">
                      <IconComponent className={`w-8 h-8 ${theme.textColor}`} />
                    </div>
                  </div>
                  
                  <div className={`flex items-center justify-between ${theme.textColor} mt-4`}>
                    <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full group-hover:bg-white/30 transition-all">
                      <span className="text-sm font-semibold">Explore Path →</span>
                    </div>
                    <span className="text-xs opacity-80 font-medium">{theme.quoteCount}+ whispers</span>
                  </div>
                </div>
                
                {/* Shine effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/0 to-transparent group-hover:via-white/20 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              </button>
            );
          })}
        </div>

        {/* About Section */}
        <div className="mt-20 max-w-3xl mx-auto text-center animate-fade-in">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#3E324D] mb-6">
            A Message From Me, To You
          </h2>
          <p className="text-[#6E6275] text-base leading-relaxed mb-6">
            I've moved through seasons that required courage-divorce, healing, motherhood, and the quiet rebuilding that shapes 
            a woman into her highest self. Those experiences didn't define me; they refined me. They taught me boundaries, 
            clarity, and the beauty of choosing myself again and again.
          </p>
          <p className="text-[#6E6275] text-base leading-relaxed mb-6 font-semibold">
            And here's what I want you to know:<br/>
            You are not what happened to you.<br/>
            You are who you're becoming because of it.
          </p>
          <p className="text-[#6E6275] text-base leading-relaxed mb-6">
            SheSafe Haven is everything I once needed, a calm space to breathe, and the reminders I needed when I was 
            becoming a stronger, wiser version of myself. Inside, you'll find carefully crafted messages to uplift, strengthen, 
            and guide you as you rise into the woman you're meant to be.
          </p>
          <p className="text-[#6E6275] text-base leading-relaxed mb-6">
            Whenever life feels heavy, or when you simply need someone who understands, this space is here, steady, safe, 
            supportive, cheering for the woman you're becoming and created with love and intention just for you.
          </p>
          <p className="text-[#6E6275] text-base leading-relaxed italic">
            With strength, grace, and sisterhood,<br/>
            <span className="signature-text text-3xl text-[#6C4CA2]">Angie</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default HomePage;